import React, { Component } from 'react';
import dateTimeHelper from '../../Helpers/datetimeHelper';
import listtableHelper from '../../Helpers/listtableHelper';
import Voldemort from '../Voldemort';
import stringHelper from '../../Helpers/stringHelper';
import TabListTableCellWidthEnum from '../../Enums/TabListTableCellWidthEnum';
import RoleService from '../../Services/roleService';
import OverWindow from './OverWindow';
import Loading from '../loading';
import ErrorMessage from '../ErrorMessage';
import moneyHelper from '../../Helpers/moneyHelper';
import { splitIntoSections, distributeIntoColumns } from '../../Helpers/detailCardLayoutHelper';
import { iconsEnabled } from '../../Helpers/iconHelper';

export default class TabListTable extends Component {
  static defaultProps = {
    caption: 'Default caption',
    data: [],
    moneyFields: [],
    moneyFieldsSecondary: [],
    dateFields: [],
    dateOnlyFields: [],
    fieldsToShow: [],
    currency: [],
    currencySecondary: [],
    boolFields: [],
    type: null,
    popupEnabled: false,
    clickOnCancel: null,
    showCaption: false,
    // The tabName of the tab to open on, when a page opens the popup for a purpose (TabOfDataTable.openDetails).
    openOnTabName: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      selectedTab: this.getTabToOpenOn(props.data),
      data: props.data,
      loading: !!(this.props.type && this.props.type.serviceFunctionForListTable),
      // The answers of the tabs that declare visibleWhen(data) -> Promise<boolean>, keyed by tabName; a tab with
      // no answer yet is still being checked (see loadAsyncTabVisibility).
      asyncTabVisibility: {},
    };
    this.isUnmounted = false;
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  // A tab whose availability needs a server round trip (e.g. the Client "Pricing History" tab) declares
  // `visibleWhen: (data) => Promise<boolean>`. The detail opens at once and the tab waits in its place with a
  // spinner, disabled; it becomes a normal tab when the answer is yes and goes away when it is no or the call
  // fails. A user without the tab's `rolekey` never sees it.
  loadAsyncTabVisibility() {
    this.props.fieldsToShow.forEach((tab) => {
      if (typeof tab.visibleWhen !== 'function') return;
      if (tab.rolekey && !RoleService.CheckUserPermission('tablisttable', tab.rolekey)) return;
      const settle = (visible) => {
        if (this.isUnmounted) return;
        this.setState((previous) => ({
          asyncTabVisibility: { ...previous.asyncTabVisibility, [tab.tabName]: visible },
        }));
      };
      // A synchronous throw has to settle the tab too, or its spinner would never stop.
      new Promise((resolve) => {
        resolve(tab.visibleWhen(this.state.data));
      })
        .then((visible) => settle(!!visible))
        .catch(() => settle(false));
    });
  }

  componentDidMount() {
    this.loadAsyncTabVisibility();
    if (this.props.type) {
      if (this.props.type.serviceFunctionForListTable) {
        this.setState({ loading: true });
        this.props.type
          .serviceFunctionForListTable(this.props.data)
          .then((result) => {
            result = new this.props.type(result);
            result.FromServiceFunctionForListTable = true;
            this.setState({
              loading: false,
              data: result,
            });
          })
          .catch((error) => {
            this.setState({
              loading: false,
            });
            ErrorMessage.SetErrorMessage('TLT59', error);
          });
      }
    } else {
      this.setState({ loading: false });
    }
  }

  // The tab called openOnTabName when the user may see it for this item, else the first tab.
  getTabToOpenOn(data) {
    const tabIndex = this.props.fieldsToShow.findIndex(
      (tab) => tab.tabName === this.props.openOnTabName && this.isTabShown(tab, data)
    );
    return tabIndex === -1 ? 0 : tabIndex;
  }

  // A tab shows when the user's roles allow it and its own hidden(data) does not hide it for this item.
  isTabShown(tab, data) {
    if (tab.rolekey && !RoleService.CheckUserPermission('tablisttable', tab.rolekey)) return false;
    return !(tab.hidden && tab.hidden(data));
  }

  renderTabs() {
    const tabs = [];
    this.props.fieldsToShow.forEach((tab, index) => {
      if (!this.isTabShown(tab, this.state.data)) return;
      const asyncVisibility = typeof tab.visibleWhen === 'function' ? this.state.asyncTabVisibility[tab.tabName] : true;
      if (asyncVisibility === false) return;
      if (asyncVisibility === undefined) {
        tabs.push(
          <button
            type='button'
            key={`tabkey${index}`}
            className='detail-tab pending'
            disabled
            aria-busy='true'
            title='Checking whether there is anything to show'
          >
            <i className='fa-solid fa-circle-notch fa-spin' />
            <span>{tab.tabLabel}</span>
          </button>
        );
        return;
      }
      tabs.push(
        <button
          type='button'
          key={`tabkey${index}`}
          className={`detail-tab${this.state.selectedTab === index ? ' on' : ''}`}
          onClick={() => this.setState({ selectedTab: index })}
        >
          {iconsEnabled() && 'icon' in tab ? <i className={tab.icon} /> : null}
          <span>{tab.tabLabel}</span>
        </button>
      );
    });
    return <div className='detail-tabs'>{tabs}</div>;
  }

  renderDataField(datatext) {
    return <span>{datatext === 'You-Know-Who' ? <Voldemort /> : datatext}</span>;
  }

  renderData(field) {
    if (!(field.name in this.state.data)) {
      if (typeof field.name !== 'function') {
        return field.name;
      }
      return field.name(this.state.data);
    }
    if (
      this.props.dateOnlyFields &&
      Array.isArray(this.props.dateOnlyFields) &&
      this.props.dateOnlyFields.includes(field.name)
    )
      return dateTimeHelper.toYYYYsMMsDD(this.state.data[field.name]);

    if (this.props.dateFields.includes(field.name))
      return dateTimeHelper.toYYYYsMMsDDsHHsMM(this.state.data[field.name]);

    if (
      this.props.type &&
      this.props.type.numberWithTwoDecimalPlaces &&
      this.props.type.numberWithTwoDecimalPlaces.includes(field.name)
    ) {
      return listtableHelper.valueWithTwoDecimalPlaces(this.state.data[field.name]);
    }
    if (this.props.moneyFields.includes(field.name)) {
      return this.state.data.hasOwnProperty(this.props.currency)
        ? moneyHelper.toMoneyFormat(this.state.data[field.name], this.state.data[this.props.currency])
        : moneyHelper.toMoneyFormat(this.state.data[field.name], this.props.currency);
    }
    if (this.props.moneyFieldsSecondary.includes(field.name)) {
      return moneyHelper.toMoneyFormat(this.state.data[field.name], this.props.currencySecondary);
    }
    if (this.props.boolFields.includes(field.name)) return listtableHelper.boolValueAsText(this.state.data[field.name]);

    if (field.contentHasHtml) {
      // eslint-disable-next-line react/no-danger
      return <div dangerouslySetInnerHTML={{ __html: this.state.data[field.name] }} />;
    }

    return this.renderDataField(this.state.data[field.name]);
  }

  isFullWidthField(field) {
    const W = TabListTableCellWidthEnum;
    if (field.width === W.Table || field.width === W.TwoLines || field.width === W.Full || field.width === W.Double) {
      return true;
    }
    if (this.props.type && this.props.type.ObjectArrayFields) {
      return this.props.type.ObjectArrayFields.some((e) => e.name === field.name);
    }
    return false;
  }

  // Table / object-array fields render their raw content full-width (no label row);
  // everything else full-width (TwoLines/Full/Double) is long/wide text.
  isTableField(field) {
    return field.width === TabListTableCellWidthEnum.Table || this.isObjectArrayField(field);
  }

  isObjectArrayField(field) {
    return !!(
      this.props.type &&
      this.props.type.ObjectArrayFields &&
      this.props.type.ObjectArrayFields.some((e) => e.name === field.name)
    );
  }

  objectArrayConfig(field) {
    return this.props.type.ObjectArrayFields.find((e) => e.name === field.name);
  }

  renderDefRow(field, key) {
    if (field.rolekey && !RoleService.CheckUserPermission('tablisttable', field.rolekey)) return null;
    if ('condition' in field && this.state.data[field.condition.field] !== field.condition.value) return null;

    const value = this.renderData(field);
    const isMoney = this.props.moneyFields.includes(field.name) || this.props.moneyFieldsSecondary.includes(field.name);
    const raw = this.state.data[field.name];
    const isEmpty = raw === null || raw === undefined || raw === '';

    // Money values are formatted "CODE amount" (e.g. "EUR 0.00"). Render the
    // currency code in a de-emphasized span so the amount stays prominent.
    let display = value;
    if (isMoney && typeof value === 'string') {
      const sp = value.indexOf(' ');
      if (sp > 0) {
        display = (
          <>
            <span className='cur'>{value.slice(0, sp)}</span> {value.slice(sp + 1)}
          </>
        );
      }
    }

    return (
      <div
        className='def-row'
        key={`row${key}`}
        title={field.tooltipText}
      >
        <div className='k'>{field.label}</div>
        <div className={`v${isMoney ? ' money' : ''}${isEmpty ? ' muted' : ''}`}>{display}</div>
      </div>
    );
  }

  // One field of an object-array item, rendered as a design-system def-row.
  // Mirrors renderDefRow: a long field (Roles etc.) stacks label-over-value; a
  // label-only field (e.g. the "Edit User" link) becomes a single full-width
  // action row with no key/value split.
  renderObjectArrayDefRow(item, obfield, key) {
    if (obfield.rolekey && !RoleService.CheckUserPermission('tablisttable', obfield.rolekey)) return null;

    const label = typeof obfield.label === 'function' ? obfield.label(item, this.state.data) : obfield.label;

    if (!('name' in obfield)) {
      return (
        <div
          className='def-row def-row-stacked'
          key={`oarow${key}`}
        >
          <div className='v v-long'>{label}</div>
        </div>
      );
    }

    const value = this.renderObjectArrayData(item, obfield);
    const W = TabListTableCellWidthEnum;
    const isLong = obfield.width === W.Full || obfield.width === W.TwoLines || obfield.width === W.Double;
    const raw = typeof obfield.name === 'string' ? item[obfield.name] : undefined;
    const isEmpty = raw === null || raw === undefined || raw === '';

    if (isLong) {
      return (
        <div
          className='def-row def-row-stacked'
          key={`oarow${key}`}
        >
          <div className='k'>{label}</div>
          <div className='v v-long'>{value}</div>
        </div>
      );
    }

    return (
      <div
        className='def-row'
        key={`oarow${key}`}
      >
        <div className='k'>{label}</div>
        <div className={`v${isEmpty ? ' muted' : ''}`}>{value}</div>
      </div>
    );
  }

  renderObjectArrayData(item, field) {
    if (!(field.name in item)) {
      if (typeof field.name !== 'function') {
        return field.name;
      }

      return field.name(item, this.state.data);
    }

    return item[field.name];
  }

  // Object-array fields (e.g. a user's Related Users) render in the design system,
  // mirroring renderActiveTab: a section band names the group, each item gets an
  // ordinal sub-band ("1st User", "2nd User"), its short fields are distributed
  // into 3 columns of def-cards so label/value pairs stay close, and long fields
  // (Roles etc.) render as full-width stacked cards.
  renderOjectArrayField(field, ob) {
    const W = TabListTableCellWidthEnum;
    const isLong = (f) => f.width === W.Full || f.width === W.TwoLines || f.width === W.Double;

    const blocks = [];
    blocks.push(
      <div
        className='def-band'
        key={`oaband${field.name}`}
      >
        <span>{field.label}</span>
      </div>
    );

    this.state.data[field.name].forEach((item, index) => {
      blocks.push(
        <div
          className='def-band oa-sub'
          key={`oasub${index}`}
        >
          <span>{`${stringHelper.ordinalNumber(index + 1)} ${ob.label}`}</span>
        </div>
      );

      const visible = ob.fieldsToShow.filter(
        (f) => !f.rolekey || RoleService.CheckUserPermission('tablisttable', f.rolekey)
      );
      const shortFields = visible.filter((f) => !isLong(f));
      const longFields = visible.filter((f) => isLong(f));

      distributeIntoColumns(shortFields, 3).forEach((colFields, ci) => {
        blocks.push(
          <div
            className='def-card'
            key={`oacard${index}-${ci}`}
          >
            {colFields.map((obfield, fi) => this.renderObjectArrayDefRow(item, obfield, `${index}-${ci}-${fi}`))}
          </div>
        );
      });

      longFields.forEach((obfield, li) => {
        blocks.push(
          <div
            className='def-card def-full'
            key={`oalong${index}-${li}`}
          >
            {this.renderObjectArrayDefRow(item, obfield, `${index}-long-${li}`)}
          </div>
        );
      });
    });

    return <div className='def-cols'>{blocks}</div>;
  }

  // A "list tab" is one whose only visible field is a full-width table (e.g. the Client's
  // Opportunities / Projects / Assignments tabs). These fill the detail panel edge-to-edge and
  // are height-fitted by TabOfDataTable.adjustTableHeight; field-form tabs keep their padding.
  isActiveTabList() {
    const activeTab = this.props.fieldsToShow[this.state.selectedTab];
    if (!activeTab || !Array.isArray(activeTab.data)) return false;
    const visible = activeTab.data.filter(
      (field) => !field.hidden || (typeof field.hidden === 'function' && !field.hidden(this.state.data))
    );
    return visible.length === 1 && visible[0].width === TabListTableCellWidthEnum.Table;
  }

  renderActiveTab() {
    const activeTab = this.props.fieldsToShow[this.state.selectedTab];
    if (!activeTab) return null;

    // A tab can bring its own renderer (e.g. PlanningDetailsItem's snapshot summary)
    // instead of the field-driven layout below.
    if (typeof activeTab.renderContent === 'function') {
      return activeTab.renderContent(this.state.data);
    }

    const visibleFields = activeTab.data.filter(
      (field) => !field.hidden || (typeof field.hidden === 'function' && !field.hidden(this.state.data))
    );

    const sections = splitIntoSections(visibleFields);
    const blocks = [];

    sections.forEach((section, si) => {
      if (section.band) {
        blocks.push(
          <div
            className='def-band'
            key={`band${si}`}
          >
            <span>{section.band}</span>
          </div>
        );
      }

      const fullWidth = section.fields.filter((f) => this.isFullWidthField(f));
      const normal = section.fields.filter((f) => !this.isFullWidthField(f));

      const columns = distributeIntoColumns(normal, 3);
      columns.forEach((colFields, ci) => {
        blocks.push(
          <div
            className='def-card'
            key={`card${si}-${ci}`}
          >
            {colFields.map((field, fi) => this.renderDefRow(field, `${si}-${ci}-${fi}`))}
          </div>
        );
      });

      fullWidth.forEach((field, fi) => {
        if (this.isTableField(field)) {
          blocks.push(
            <div
              className='def-full'
              key={`full${si}-${fi}`}
            >
              {this.isObjectArrayField(field)
                ? this.renderOjectArrayField(field, this.objectArrayConfig(field))
                : this.renderData(field)}
            </div>
          );
          return;
        }
        // long/wide field (TwoLines/Full/Double): full-width card, label on top,
        // value left-aligned below so long text reads naturally.
        if (field.rolekey && !RoleService.CheckUserPermission('tablisttable', field.rolekey)) return;
        if ('condition' in field && this.state.data[field.condition.field] !== field.condition.value) return;
        // An empty-label full-width field is a bare action link (e.g. the per-facet
        // "Edit ..." rows), not a labelled value — render it chrome-less (no card
        // border, no empty label cell) so the DS link stands on its own.
        if (!field.label) {
          blocks.push(
            <div
              className='def-full def-action'
              key={`full${si}-${fi}`}
            >
              {this.renderData(field)}
            </div>
          );
          return;
        }
        blocks.push(
          <div
            className='def-card def-full'
            key={`full${si}-${fi}`}
          >
            <div className='def-row def-row-stacked'>
              <div className='k'>{field.label}</div>
              <div className='v v-long'>{this.renderData(field)}</div>
            </div>
          </div>
        );
      });
    });

    return <div className='def-cols'>{blocks}</div>;
  }

  render() {
    const caption =
      this.props.type && this.props.type.nameOfTheItemDetailList
        ? this.props.type.nameOfTheItemDetailList(this.state.data)
        : this.props.caption;
    const content = this.state.loading ? (
      <Loading />
    ) : (
      <div className='bravo-ds'>
        {this.props.fieldsToShow.length > 1 ? this.renderTabs() : null}
        <div className={`detail-body${this.isActiveTabList() ? ' detail-body-list' : ''}`}>
          {this.renderActiveTab()}
        </div>
      </div>
    );
    if (this.props.popupEnabled) {
      const idPill =
        this.props.type && typeof this.props.type.NameOfTheIdField === 'function'
          ? this.state.data[this.props.type.NameOfTheIdField()]
          : this.props.selected;
      // A tab can declare an editAction { label, rolekey, onClick(item) }; the active
      // tab's action becomes a header button next to the close (X), so the per-facet
      // "Edit ..." link no longer lives at the bottom of the panel. Role-gated the same
      // way the inline links were (RoleService group 'tablisttable').
      const activeTab = this.props.fieldsToShow[this.state.selectedTab];
      const editAction = activeTab && activeTab.editAction;
      const headerAction =
        editAction && (!editAction.rolekey || RoleService.CheckUserPermission('tablisttable', editAction.rolekey))
          ? { label: editAction.label, onClick: () => editAction.onClick(this.state.data) }
          : null;
      return (
        <OverWindow
          title={caption}
          idPill={idPill}
          headerAction={headerAction}
          showMenuIcon={true}
          clickOnCancel={this.props.clickOnCancel}
          iconName='fa-solid fa-edit'
          closeOnClickOutside={false}
          //minHeight={700}
          verticalScroll={true}
        >
          {content}
        </OverWindow>
      );
    }

    return <div>{content}</div>;
  }
}
